import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { MonthlyEnergyModel } from "../../entities/monthlyEnergy";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";

export const monthlyEnergyApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMonthlyEnergyCommonInfo: builder.query<EntityCommonInfoModel, boolean>({
      query: (isCumulative: boolean) => `/monthlyEnergy/${isCumulative}/commonInfo`,
      providesTags: [{ type: TagTypes.MonthlyEnergy, id: "LIST" }],
    }),
    getMonthlyEnergyFilteredPaged: builder.query<FilteredPagedQueryResult<MonthlyEnergyModel>, FilteredPagedModel>({
      query: ({ isCumulative, page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/monthlyEnergy/${isCumulative}/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.MonthlyEnergy, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.MonthlyEnergy,
                id,
              })),
            ]
          : [{ type: TagTypes.MonthlyEnergy, id: "PARTIAL-LIST" }],
    }),
    getMonthlyEnergy: builder.query<MonthlyEnergyModel, number>({
      query: (monthlyEnergyId) => `/monthlyEnergy/${monthlyEnergyId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.MonthlyEnergy, id: arg }],
    }),
    scrapMonthlyEnergy: builder.query<MonthlyEnergyModel, { isCumulative: boolean; tracingNumber: string }>({
      query: ({ isCumulative, tracingNumber }) => ({
        url: `/monthlyEnergy/scrap/${isCumulative}`,
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.MonthlyEnergy, id: "Scrap" }],
    }),
    addMonthlyEnergy: builder.mutation({
      query: (initialMonthlyEnergy) => ({
        url: "/monthlyEnergy",
        method: "POST",
        body: initialMonthlyEnergy,
      }),
      invalidatesTags: [
        { type: TagTypes.MonthlyEnergy, id: "LIST" },
        { type: TagTypes.MonthlyEnergy, id: "PARTIAL-LIST" },
      ],
    }),
    editMonthlyEnergy: builder.mutation({
      query: (monthlyEnergy) => ({
        url: `/monthlyEnergy/${monthlyEnergy.id}`,
        method: "PUT",
        body: monthlyEnergy,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.MonthlyEnergy, id: arg.id }],
    }),
    deleteMonthlyEnergy: builder.mutation({
      query: (monthlyEnergyId) => ({
        url: `/monthlyEnergy/${monthlyEnergyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.MonthlyEnergy, id: arg },
              { type: TagTypes.MonthlyEnergy, id: "LIST" },
            ],
    }),
    deleteMultiMonthlyEnergies: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/monthlyEnergy/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.MonthlyEnergy,
                id,
              })),
              { type: TagTypes.MonthlyEnergy, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetMonthlyEnergyCommonInfoQuery,
  useGetMonthlyEnergyFilteredPagedQuery,
  useGetMonthlyEnergyQuery,
  useScrapMonthlyEnergyQuery,
  useLazyScrapMonthlyEnergyQuery,
  useAddMonthlyEnergyMutation,
  useEditMonthlyEnergyMutation,
  useDeleteMonthlyEnergyMutation,
  useDeleteMultiMonthlyEnergiesMutation,
} = monthlyEnergyApi;
